import { useCallback } from 'react'
import { CompositeImage } from '../../../interfaces/notification-field-suggestions'
import { getFileBlobFromUrl, getImageFromBlob } from '../../../_utils/images'
import { generateUID } from '../../../_utils/uid'
import { noop } from '../../../_utils/utils'
import { PinturaInternals } from '@pushly/aqe/lib/components/file-uploader/utils'
import { useService } from '../../../hooks/use-service'
import { FileUploadService } from '@pushly/aqe/lib/services/file-upload.service'
import { DomainDto } from '../../../dtos/domain'

const { processImage, createDefaultImageReader, createDefaultImageWriter } = PinturaInternals

// Image quality is applied by the upload service from the owner's settings, so the loader only
// needs to know which domain it belongs to.
type MediaDomain = Pick<DomainDto, 'id'>

/**
 * Who the processed suggested image is stored under. A domain send stores it under that domain; an
 * org send stores it under the org so every domain in the audience can use the asset.
 */
export type MediaUploadOwner = {
    type: 'domain' | 'account'
    id: number
}

export const useCompositeImageLoader = ({ id: domainId }: MediaDomain) => {
    const fileUploadSvc = useService(FileUploadService)

    return useCallback(
        async (imageURL: string, owner?: MediaUploadOwner): Promise<CompositeImage | null> => {
            if (!imageURL) return null

            const uploadOwner: MediaUploadOwner = owner ?? { type: 'domain', id: domainId }

            try {
                const blob = await getFileBlobFromUrl(imageURL)
                if (!blob) {
                    return null
                }

                const img = await getImageFromBlob(blob)

                const imageCropAspectRatio = 2
                let width = img.width
                let height = img.height

                if (width / height !== imageCropAspectRatio) {
                    if (width > height) {
                        width =
                            img.width / img.height > imageCropAspectRatio
                                ? img.height * imageCropAspectRatio
                                : img.width
                    }

                    height = width / imageCropAspectRatio
                }

                const processed = await processImage(blob as any, {
                    imageReader: createDefaultImageReader(),
                    imageWriter: createDefaultImageWriter({
                        targetSize: {
                            width: 400,
                            height: 200,
                            fit: 'cover',
                            upscale: false,
                        },
                    }),
                    imageCropAspectRatio: 2,
                    imageCrop: {
                        x: img.width / 2 - width / 2,
                        y: img.height / 2 - height / 2,
                        width,
                        height,
                    },
                    imageTargetSize: {
                        width: 400,
                        height: 200,
                    },
                })

                const uploaded = await fileUploadSvc.processFormDataUpload(
                    'image',
                    uploadOwner.type,
                    uploadOwner.id,
                    'file',
                    processed.dest,
                    'public-read',
                    {
                        tmpId: generateUID(),
                        originalMediaUrl: imageURL,
                        progressive: true,
                        imageType: 'notification-image',
                    },
                    noop,
                )

                return {
                    ...processed,
                    url: uploaded.url,
                    filename: uploaded.filename,
                    originalMediaUrl: imageURL,
                }
            } catch (err) {
                console.error('Suggested Image Loading Error', err)
            }

            return null
        },
        [domainId, fileUploadSvc],
    )
}
